/**
 * Higieniza um SVG antes de ele entrar na página.
 *
 * **Por que isto existe.** O pinout gráfico precisa ser clicável — clicar num
 * componente do desenho abre o painel e navega, como no Sistema de hoje. Uma
 * imagem não expõe o que tem dentro, então o SVG tem de ir para o DOM. E aí ele
 * deixa de ser figura e passa a ser markup da página: `<script>` ali dentro roda
 * com os mesmos direitos do resto do Sistema.
 *
 * Isso não é hipótese. O motor que gera o desenho **concatena texto do banco sem
 * escapar** — `sb.Append("...>" + pin.pino + "</text>")`, em
 * `DesenharPinoutGraficoPassos.cs`. Descrição de componente, conector, circuito
 * e pino são campos de texto livre. Nenhum dado real tem `<` hoje (amostrei seis
 * projetos), mas basta alguém digitar.
 *
 * **Isto continua valendo depois de a API escapar.** A tela não deveria confiar
 * que todo gerador, hoje e amanhã, escapou certo — é uma linha de defesa barata
 * no lugar onde o estrago aconteceria.
 *
 * O que sai: `script`, `foreignObject` (que embute HTML arbitrário dentro do
 * SVG), qualquer atributo `on*`, e toda referência externa — só `#ancora` local
 * sobrevive. O desenho do pinout não usa nada disso; se um dia usar, quebra
 * visível, e quebrar visível é melhor que executar calado.
 */

/** Elementos que não têm o que fazer num desenho e podem executar. */
const PROIBIDOS = ["script", "foreignObject", "iframe", "embed", "object", "animate", "set"]

/** Atributos que apontam para fora — só sobrevive o que começa com `#`. */
const REFERENCIAS = ["href", "xlink:href", "src", "data"]

export type SvgHigienizado = {
  elemento: SVGSVGElement
  /** O que foi removido. Zero é o normal; qualquer outro número merece um olhar. */
  removidos: number
}

/**
 * O `&` que não começa uma entidade. O motor escreve o nome do componente sem
 * escapar, e há nome com `&` ("SIG - See Indicators & Gauges"): o XML quebrava e
 * a montagem mostrava "sem traço" no lugar do componente. Esse caso tem uma
 * leitura só, então é corrigido em vez de recusado; `&amp;` e `&#233;` ficam.
 */
const E_COMERCIAL_SOLTO = /&(?!(?:[A-Za-z][A-Za-z0-9]*|#[0-9]+|#[xX][0-9A-Fa-f]+);)/g

/**
 * Devolve o SVG pronto para entrar no DOM, ou lança se o texto não for um SVG
 * válido. O `&` solto não conta: ele é escapado antes da leitura.
 */
export function higienizarSvg(texto: string): SvgHigienizado {
  const doc = new DOMParser().parseFromString(texto.replace(E_COMERCIAL_SOLTO, "&amp;"), "image/svg+xml")

  // O DOMParser não lança: em erro ele devolve um documento com <parsererror>.
  const falha = doc.querySelector("parsererror")
  if (falha) {
    throw new Error(
      "O desenho veio com XML inválido e não pôde ser exibido. " +
      "Costuma ser um < sem escapar numa descrição de componente, conector ou circuito.",
    )
  }

  const raiz = doc.documentElement as unknown as SVGSVGElement
  if (!raiz || raiz.nodeName.toLowerCase() !== "svg") {
    throw new Error("O desenho não é um SVG.")
  }

  let removidos = 0

  for (const nome of PROIBIDOS) {
    for (const el of Array.from(raiz.querySelectorAll(nome))) {
      el.remove()
      removidos += 1
    }
  }

  // Percorre tudo, inclusive a raiz, tirando manipuladores e referências externas.
  const todos: Element[] = [raiz, ...Array.from(raiz.querySelectorAll("*"))]
  for (const el of todos) {
    for (const attr of Array.from(el.attributes)) {
      const nome = attr.name.toLowerCase()
      if (nome.startsWith("on")) {
        el.removeAttribute(attr.name)
        removidos += 1
        continue
      }
      if (REFERENCIAS.includes(nome) && !attr.value.trim().startsWith("#")) {
        el.removeAttribute(attr.name)
        removidos += 1
      }
    }
  }

  return { elemento: raiz, removidos }
}
